import React from 'react';
import { observer } from 'mobx-react';
import { observable, computed } from 'mobx';
import ErrorBoundary from 'components/hoc/error_boundary.js';
import styled from '@emotion/styled';
import { AppUI } from '@seedlang/state';
import { MembershipGroupStore, SubscriptionStore, UserStore, GroupStore, UserGroupStore, MembershipTypeStore , RssFeedStore , DevPaymentMigrationStore, ScheduledSubscriptionStore } from '@seedlang/stores';
import autobind from 'autobind-decorator';
import { Link } from 'react-router';
import InPlaceCheckbox from 'components/form/in_place_checkbox';
import InPlaceSelect from 'components/form/in_place_select';
import DeleteButton from 'components/button/delete_button';
import { isPresent, isBlank } from '@seedlang/utils';
import { Constants } from '@seedlang/constants';
import SubscriptionCreate from 'pages/builder/subscriptions/subscription_create';
import Cookies from 'js-cookie';
import Text from 'components/text';
import CampaignMessageIndex from 'pages/builder/campaign_messages/campaign_message_index';
import InPlaceDatePicker from 'components/form/in_place_date_picker';
import ContactMessageIndex from 'pages/builder/contact_messages/contact_message_index';
import Alert from 'components/alert';
import InfoTooltip from 'components/info_tooltip';
import Stack from 'components/layout/stack';
import Grid from 'components/layout/grid';
import ButtonPrimary from 'components/button/button_primary';
import LinkWithCopy from 'components/link_with_copy';

const Note = styled.div`
  background: white;
  margin-top: 10px;
  padding: 10px;
  font-size: 14px;
`;

const IconWrapper = styled.span`
  .fa-file-text {
    color: #333;
    cursor: pointer;
    margin-right: 4px;
  }
`;

const Item = styled.div`
  font-size: 14px;
  line-height: 18px;
`;

@observer
class UsersEdit extends React.Component {
  @observable groupId = Constants.EASY_GERMAN_GROUP_ID;
  @observable spinnerForRssFeedId = null;
  @observable showUpdateMailerLiteMessage = false;

  @computed get group() {
    return GroupStore.hasIndexData ? GroupStore.indexData.filter(item => item.id === AppUI.site.groupId)[0] : null;
  }

  @computed get userGroup() {
    return UserStore.hasShowData && UserStore.showData.userGroups.find(item => item.group?.id === AppUI.site.groupId);
  }

  @computed get filterMembershipTypes() {
    const siteId = Cookies.get('site_id');

    return MembershipTypeStore.indexData.filter(item => item.siteId === siteId);
  }

  @computed get hasMultipleUserGroups() {
    return UserStore.hasShowData && UserStore.showData.userGroups.length > 1;
  }

  constructor(props) {
    super(props);
    this.loadUser();
    this.loadSubscriptions();
    this.loadScheduledSubscriptions();
    this.loadRssFeeds();
    GroupStore.getIndex();
    if (!MembershipTypeStore.hasIndexData) {
      this.getMembershipTypes();
    }
    if (!MembershipGroupStore.hasIndexData) {
      MembershipGroupStore.getIndex();
    }
  }

  componentWillUnmount() {
    SubscriptionStore.clearIndexData();
    ScheduledSubscriptionStore.clearIndexData();
  }

  @autobind loadUser() {
    UserStore.getShow({ids: {expand_user: true, userId: this.props.params.userId}});
  }

  @autobind onRemoveUser() {
    if (this.hasMultipleUserGroups) {
      UserGroupStore.destroy({ids: {userGroupId: this.userGroup?.id}}, this.afterRemoveUser);
    } else {
      UserStore.destroy({ids: {userId: this.props.params.userId}}, this.afterRemoveUser);
    }
  }

  @autobind afterRemoveUser() {
    AppUI.routeStore.routeToNamed('creator.users.index', {groupId: AppUI.groupId});
  }

  @autobind getMembershipTypes() {
    MembershipTypeStore.getIndex();
  }

  @autobind loadSubscriptions() {
    SubscriptionStore.getIndex({queryStrings: {user_id: this.props.params.userId}});
  }

  @autobind loadScheduledSubscriptions() {
    ScheduledSubscriptionStore.getIndex({queryStrings: {user_id: this.props.params.userId}});
  }

  @autobind onDeleteScheduledSubscription(scheduledSubscription) {
    ScheduledSubscriptionStore.destroy({
      ids: {scheduledSubscriptionId: scheduledSubscription.id},
      queryStrings: {user_id: this.props.params.userId},
    }, this.loadScheduledSubscriptions);
  }

  @autobind loadRssFeeds() {
    RssFeedStore.getIndex({ queryStrings: { user_id: this.props.params.userId } }, resp => {
      RssFeedStore.setIndexData(resp);
      this.spinnerForRssFeedId = null;
    });
  }

  @autobind onUpdateFeed(rssFeed) {
    this.spinnerForRssFeedId = rssFeed.id;
    RssFeedStore.updateFeed({ ids: { rssFeedId: rssFeed.id, user_id: this.props.params.userId } }, this.loadRssFeeds);
  }

  @autobind onRegenerateUrl(rssFeed) {
    this.spinnerForRssFeedId = rssFeed.id;
    RssFeedStore.regenerateUrl({ ids: { rssFeedId: rssFeed.id, user_id: this.props.params.userId } }, this.loadRssFeeds);
  }

  @autobind onDevInitializePaymentMigration() {
    DevPaymentMigrationStore.create({data: {siteId: AppUI.site.id, userId: this.props.params.userId}}, this.loadUser);
  }

  @autobind displayBoolean(value) {
    if (isBlank(value)) {
      return 'Not Set';
    }
    return typeof value === 'string' ? (value === 'true' ? '✅' : '❌') : value === true ? '✅' : value === false ? '❌' : value;
  }

  @autobind onUpdateMailerlite() {
    this.showUpdateMailerLiteMessage = true;
    setTimeout(() => {
      this.showUpdateMailerLiteMessage = false;
    }, 5000);
    AppUI.userStore.updateMailerlite({ids: {userId: this.props.params.userId}}, this.loadUser);
  }

  @computed get paymentMigrationFrequency() {
    const membership = UserStore.showData.paymentMigration?.membership;
    if (!membership) return;
    return membership?.numberMonths === 12 ? 'Yearly' : 'Monthly';
  }

  @computed get passwordResetLink() {
    if (!UserStore.showData.resetPasswordToken) return;
    return `https://${AppUI.site.domainString}/user/password/edit?reset_password_token=${UserStore.showData.resetPasswordToken}`;
  }

  render() {
    return (
      <Stack gap='sm' alignItems='stretch'>
        <div className='breadcrumbs'>
          <Link
            to={{name: 'creator.users.index', params: {groupId: AppUI.groupId}}}
          >
            All Users
          </Link>
        </div>
        {
          UserStore.hasShowData &&
            <div>
              <Grid columns={3}>
                <fieldset>
                  <legend>Name</legend>
                  {UserStore.showData.name || '-'}
                </fieldset>
                <fieldset>
                  <legend>Email</legend>
                  <div>
                    {UserStore.showData.email || '-'}
                  </div>
                  <div>
                    <InPlaceCheckbox
                      model='users'
                      field='confirmed'
                      id={UserStore.showData.id}
                      value={UserStore.showData.confirmed}
                      afterChange={this.loadUser}
                    >
                      Email confirmed
                    </InPlaceCheckbox>
                  </div>
                </fieldset>
                <fieldset>
                  <legend>Role <InfoTooltip><div>This only serves as a way of displaying the role</div><div>of the team member, and currently has no role</div><div>in assigning user rights.</div></InfoTooltip></legend>
                  <InPlaceSelect
                    includeBlank
                    id={UserStore.showData.id}
                    value={UserStore.showData.role}
                    model='users'
                    field='role'
                    options={Constants.USER_ROLES}
                    afterChange={this.loadUser}
                  />
                </fieldset>
              </Grid>
              <Grid columns={2}>
                <fieldset style={{display: 'flex', alignItems: 'center', gap: 5}}>
                  <legend>Membership Group <InfoTooltip><div>Users are assigned a membership group when a user is created</div>or added to this site. If you want to change the <div>membership group, you can do so from the "Membership Groups" page.</div></InfoTooltip></legend>
                  <InPlaceSelect
                    model='user_groups'
                    id={this.userGroup?.id}
                    field='membership_group_id'
                    value={this.userGroup?.membershipGroupId}
                    options={MembershipGroupStore.indexData.map(item => [item.id, item.name])}
                    afterChange={this.loadUser}
                  />
                </fieldset>
                {
                  isPresent(this.userGroup) &&
                    <fieldset style={{display: 'flex', alignItems: 'center', gap: 5}}>
                      <legend>Free Membership <InfoTooltip>Add a date if you want the free membership to expire.</InfoTooltip></legend>
                      <InPlaceCheckbox
                        model='user_groups'
                        id={this.userGroup.id}
                        field='permanent'
                        value={this.userGroup.permanent}
                        afterChange={this.loadUser}
                      >
                        Free Membership
                      </InPlaceCheckbox>
                      <InPlaceSelect
                        includeBlank
                        model='user_groups'
                        id={this.userGroup.id}
                        field='membership_type_id'
                        value={this.userGroup.membershipTypeId}
                        options={this.filterMembershipTypes.map(item => [item.id, item.name])}
                        afterChange={this.loadUser}
                      />
                      <InPlaceDatePicker
                        model='user_groups'
                        id={this.userGroup.id}
                        field='permanent_until'
                        value={this.userGroup.permanentUntil}
                        afterChange={this.loadUser}
                      />
                    </fieldset>
                }
              </Grid>
          </div>
        }
        <Grid columns={3}>
          <fieldset>
            <legend>Agreements <InfoTooltip><div>This shows whether the user has agreed to receive</div><div>emails and the terms of service.</div></InfoTooltip></legend>
            <Item>Email: {this.displayBoolean(this.userGroup?.userAgreedToEmail)}</Item>
            <Item>Terms: {this.displayBoolean(this.userGroup?.userAgreedToTerms)}</Item>
          </fieldset>
          {
            UserStore.hasShowData &&
              <fieldset>
                <legend>Timestamps</legend>
                {
                  UserStore.showData?.createdAt?.formattedDateWithYear !== this.userGroup?.createdAt?.formattedDateWithYear &&
                    <Item>
                      Created on Seedlang: {UserStore.showData?.createdAt?.formattedDateWithYear}
                    </Item>
                }
                <Item>
                  Created on Site: {this.userGroup?.createdAt?.formattedDateWithYear}
                </Item>
                <Item>
                  Last Active on Site: {this.userGroup?.lastActiveAt ? this.userGroup?.lastActiveAt.formattedDateWithYear : '-'}
                </Item>
              </fieldset>
          }
          <fieldset>
          {
            isPresent(this.userGroup) &&
            <>
              <legend>Early Access</legend>
              <InPlaceCheckbox
                model='user_groups'
                id={this.userGroup.id}
                field='early_access'
                value={this.userGroup.earlyAccess}
                afterChange={this.loadUser}
              >
                Early Access
              </InPlaceCheckbox>
            </>
          }
          </fieldset>
        </Grid>
        <fieldset>
          <legend>Subscriptions</legend>
          {
            AppUI.user.developer &&
              <SubscriptionCreate
                userId={UserStore.showData.id}
                afterCreate={this.loadSubscriptions}
              />
          }
          {
            SubscriptionStore.hasIndexData &&
              <table
                className='table-wrapper'
              >
                <thead>
                  <tr>
                    <th />
                    <th>Type</th>
                    <th>Start</th>
                    <th>End</th>
                    <th>Cancelled At</th>
                    <th>Amount</th>
                    <th>Cncl</th>
                    <th>Ref</th>
                    <th>Pnd</th>
                    <th>#Pay</th>
                  </tr>
                </thead>
                <tbody>
                  {
                    SubscriptionStore.indexData.map(item => {
                      return (
                        <tr>
                          <td>
                            <Link
                              to={{
                                name: 'creator.subscriptions.edit',
                                params: { groupId: AppUI.groupId, subscriptionId: item.id },
                              }}
                              className='on-click'
                            >
                              <i className='fa fa-chevron-right fa-block' />
                            </Link>
                          </td>
                          <td>
                            {item.membership?.title?.replace(' Membership', '') || '-'}
                          </td>
                          <td>
                            <div className='text'>
                              {item.currentPeriodStart.formattedDateWithYear}
                            </div>
                          </td>
                          <td>
                            <div className='text'>
                              {item.currentPeriodEnd.formattedDateWithYear}
                            </div>
                          </td>
                          <td>
                            <div>{item.cancelledAt && item.cancelledAt.formattedDateWithYear}</div>
                            <div>{item.cancellationMessage && <IconWrapper onClick={() => this.cancellationMessage = item.cancellationMessage}><i className='fa fa-file-text' /></IconWrapper>}{item.cancellationReason}</div>
                          </td>
                          <td>
                            <div>{item.amountFormatted}</div>
                            <div>{item.paymentMethod}</div>
                          </td>
                          <td>
                            <div className='text'>
                              <InPlaceCheckbox
                                model='subscriptions'
                                field='cancel_at_period_end'
                                id={item.id}
                                value={item.cancelAtPeriodEnd}
                              />
                            </div>
                          </td>
                          <td>
                            <div className='text'>
                              <InPlaceCheckbox
                                model='subscriptions'
                                field='refunded'
                                id={item.id}
                                value={item.refunded}
                              />
                            </div>
                          </td>
                          <td>
                            <div className='text'>
                              <InPlaceCheckbox
                                model='subscriptions'
                                field='pending'
                                id={item.id}
                                value={item.pending}s
                              />
                            </div>
                          </td>
                          <td>
                            {item.paymentsCount}
                          </td>
                        </tr>
                      );
                    })
                  }
                </tbody>
              </table>
          }
        </fieldset>
        <fieldset>
          <legend>Scheduled Subscription Changes</legend>
          {
            ScheduledSubscriptionStore.hasIndexData && ScheduledSubscriptionStore.indexData.length > 0 &&
              <table
                className='table-wrapper'
              >
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Start</th>
                    <th>End</th>
                    <th>Amount</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {
                    ScheduledSubscriptionStore.indexData.map(item => {
                      return (
                        <tr key={item.id}>
                          <td>
                            {item.membership?.title?.replace(' Membership', '') || '-'}
                          </td>
                          <td>
                            {item.currentPeriodStart?.formattedDateWithYear || '-'}
                          </td>
                          <td>
                            {item.currentPeriodEnd?.formattedDateWithYear || '-'}
                          </td>
                          <td>
                            {item.amountFormatted || `${item.currency} ${item.amount}`}
                          </td>
                          <td>
                            <DeleteButton
                              message='Are you sure you want to cancel this scheduled subscription?'
                              onConfirm={this.onDeleteScheduledSubscription.bind(this, item)}
                            />
                          </td>
                        </tr>
                      );
                    })
                  }
                </tbody>
              </table>
          }
          {
            (!ScheduledSubscriptionStore.hasIndexData || ScheduledSubscriptionStore.indexData.length === 0) &&
              <div>No scheduled subscription changes</div>
          }
        </fieldset>
        {
          UserStore.showData.paymentMigration &&
            <>
                <fieldset>
                  <legend>Payment Migration <InfoTooltip>This shows the status of the payment migration.</InfoTooltip></legend>
                  <ul style={{ fontSize: 14, listStyle: 'bullet' }}>
                    <li>
                        Status: <span style={{ fontWeight: 600 }}>{UserStore.showData.paymentMigration.friendlyStatus || 'Not Started'}</span>
                    </li>
                    <li>
                        Currency: {UserStore.showData.paymentMigration.currency}
                    </li>
                    <li>
                        Membership: {UserStore.showData.paymentMigration.membership?.title} ({this.paymentMigrationFrequency})
                    </li>
                  </ul>
                </fieldset>
              {
                AppUI.user.developer &&
                    <fieldset>
                      <legend>Payment Migration (for developers only)</legend>
                      {
                        UserStore.showData.paymentMigration?.id &&
                          <Grid columns={[6,3,2]}>
                            <InPlaceSelect
                              includeBlank
                              id={UserStore.showData.paymentMigration.id}
                              model='dev_payment_migrations'
                              field='status'
                              options={['setting_up', 'stripe_payment_setup_started', 'waiting_for_patreon_cancellation', 'waiting_for_patreon_expiry', 'waiting_for_patreon_refund', 'waiting_for_patreon_refund_timeout', 'completed']}
                              value={UserStore.showData.paymentMigration.status}
                              afterChange={this.loadUser}
                            />
                            <InPlaceSelect
                              includeBlank
                              id={UserStore.showData.paymentMigration.id}
                              model='dev_payment_migrations'
                              field='patreon_pledge_cadence'
                              options={[[12, 'Yearly'], [1, 'Monthly']]}
                              value={UserStore.showData.paymentMigration.patreonPledgeCadence}
                              afterChange={this.loadUser}
                            />
                            <InPlaceSelect
                              includeBlank
                              id={UserStore.showData.paymentMigration.id}
                              model='dev_payment_migrations'
                              field='currency'
                              options={['eur', 'usd']}
                              value={UserStore.showData.paymentMigration.currency}
                              afterChange={this.loadUser}
                            />
                          </Grid>
                        }
                        {
                          !UserStore.showData.paymentMigration?.id &&
                            <ButtonPrimary onClick={this.onDevInitializePaymentMigration}>
                              Initialize Payment Migration
                            </ButtonPrimary>
                        }
                    </fieldset>
              }
            </>
        }
        <fieldset>
          <legend>Support Emails Sent By User</legend>
          <ContactMessageIndex
            userId={this.props.params.userId}
            limit={5}
            whiteLabel
          />
        </fieldset>
        <fieldset>
          <legend>Email Notification Settings</legend>
          <div style={{display: 'flex', gap: '20px'}}>
            <InPlaceCheckbox
              model='user_groups'
              field='block_all_mailings'
              id={UserStore.showData.currentUserGroup?.id}
              value={UserStore.showData.currentUserGroup?.blockAllMailings}
              afterChange={UserStore.reloadShow.bind(UserStore)}
            >
              Block All Emails
            </InPlaceCheckbox>
            {
              isPresent(UserStore.showData.blockMailingByType) &&
                <Text fontSize='14px' style={{display: 'flex', gap: '5px'}}><label>Blocked email types:</label> {UserStore.showData.blockMailingByType}</Text>
            }
          </div>
        </fieldset>
        <fieldset>
          <legend>Emails Sent By Site</legend>
          <CampaignMessageIndex
            whiteLabel
            groupId={AppUI.groupId}
            userId={this.props.params.userId}
            siteId={AppUI.siteId}
            limit={5}
          />
        </fieldset>
        {
          AppUI.site.enableMailerlite &&
            <Grid columns={2}>
              <fieldset>
                <legend>MailerLite Sync</legend>
                {
                  this.userGroup?.mailerliteId &&
                    <div>
                      <ButtonPrimary onClick={this.onUpdateMailerlite}>Sync with Mailerlite</ButtonPrimary>
                    </div>
                }
                {
                  UserStore.hasShowData && isBlank(this.userGroup?.mailerliteId) &&
                    <Note>
                      Note: This user is not synced with Mailerlite.
                    </Note>
                }
              </fieldset>
              <fieldset>
                <legend>MailerLite Page</legend>
                {
                  this.userGroup?.mailerliteId &&
                    <div>
                      <a href={`https://dashboard.mailerlite.com/subscribers/${this.userGroup.mailerliteId}`} className='underline' target='_blank'>Mailerlite Page</a>
                    </div>
                }
                {
                  this.showUpdateMailerLiteMessage &&
                    <Alert>
                      User updated in Mailerlite
                    </Alert>
                }
              </fieldset>
            </Grid>
        }
        <fieldset>
          <legend>RSS Feeds</legend>
          <table
            className='table-wrapper'
          >
            <thead>
              <tr>
                <th>Podcast</th>
                <th>Private RSS feed URL</th>
                <th></th>
                <th>Last Updated</th>
                <th></th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {
                RssFeedStore.indexData.map(item => {
                  return (
                    <tr key={item.id}>
                      <td>
                        {item.podcast.title}
                      </td>
                      <td style={{ width: '27em'}}>
                        {item.url}
                      </td>
                      <td>
                        <DeleteButton
                          message='A new RSS URL will be generated and the existing one will stop working. Do you want to continue?'
                          faIcon='fa-recycle'
                          onConfirm={this.onRegenerateUrl.bind(this, item)}
                        />
                      </td>
                      <td style={{ width: '8em' }}>
                        {item.updatedAt.formatted}
                      </td>
                      <td style={{ width: '8em'}}>
                        <ButtonPrimary onClick={this.onUpdateFeed.bind(this, item)}>
                          Update Feed
                        </ButtonPrimary>
                      </td>
                      <td style={{ width: '6em'}}>
                        {this.spinnerForRssFeedId === item.id && <i title='Please allow 20 seconds for an update.'>Updating...</i>}
                      </td>
                    </tr>
                  );
                })
              }
            </tbody>
          </table>
        </fieldset>
        {
          this.passwordResetLink && (
            <fieldset>
              <legend>Password Reset Link</legend>
              <LinkWithCopy link={this.passwordResetLink} />
            </fieldset>
          )
        }
        <fieldset>
          <legend>{this.hasMultipleUserGroups ? 'Remove User' : 'Delete User'}</legend>
          <DeleteButton
            right
            message={`Are you sure you want to ${this.hasMultipleUserGroups ? 'remove' : 'delete'} this user? This action is not reversible.`}
            onConfirm={this.onRemoveUser}
          >
            <ButtonPrimary>
              {this.hasMultipleUserGroups ? 'Remove User' : 'Delete User'}
            </ButtonPrimary>
          </DeleteButton>
          {
            UserStore.hasShowData && UserStore.showData.userGroups.length > 1 &&
              <Note>
                This user exists on multiple sites and cannot be deleted. If they would want all of their data deleted, please contact help@seedlang.com.
              </Note>
          }
        </fieldset>
      </Stack>
    );
  }
}

export default ErrorBoundary(UsersEdit);
